import type {
  HoldingUnitAssistedMatchCandidate,
  HoldingUnitInventorySummaryItem,
  HoldingUnitLifecycleStatus,
  HoldingUnitLoadSession,
  HoldingUnitLoadSessionDiscrepancyReview,
  HoldingUnitLoadSessionGroupClaimStatus,
  HoldingUnitLoadSessionGroupTotals,
  HoldingUnitLoadSessionItem,
  HoldingUnitLoadSessionMatchCandidate,
  HoldingUnitLoadSessionMaterialGroup,
  HoldingUnitLoadSessionStatus,
  HoldingUnitReservationState,
  HoldingUnitSnapshot,
} from './holding-units.dto.js';
import type { FixedIdAssetRegistryItem } from './fixed-id-assets.dto.js';
import type { OutboundRecordReference } from '../outbound/outbound-record-reference.dto.js';

export type HoldingUnitsListMode = 'work_queue';
export type HoldingUnitsNetState = 'positive' | 'zero' | 'all';
export type HoldingUnitsWorkQueueStatusMode = 'queue' | 'open' | 'ready';
export const holdingUnitProjectionCheckpointEntityTypeValues = [
  'holdingUnit',
  'loadSession',
  'fixedIdAsset',
] as const;
export type HoldingUnitProjectionCheckpointEntityType =
  (typeof holdingUnitProjectionCheckpointEntityTypeValues)[number];
export const holdingUnitProjectionCheckpointStatusValues = [
  'pending',
  'processing',
  'current',
  'failed',
  'dead_letter',
] as const;
export type HoldingUnitProjectionCheckpointStatus =
  (typeof holdingUnitProjectionCheckpointStatusValues)[number];

export interface HoldingUnitProjectionCheckpointStatusCounts {
  pending: number;
  processing: number;
  current: number;
  failed: number;
  dead_letter: number;
}

export type HoldingUnitProjectionCheckpointCountsByEntityType = Record<
  HoldingUnitProjectionCheckpointEntityType,
  HoldingUnitProjectionCheckpointStatusCounts
>;

export interface HoldingUnitProjectionCheckpointDto {
  id: string;
  companyId: string;
  entityType: HoldingUnitProjectionCheckpointEntityType;
  entityId: string;
  projectedVersion: number;
  targetVersion: number;
  status: HoldingUnitProjectionCheckpointStatus;
  attempts: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  lastErrorStage: string | null;
  lastErrorAt: string | null;
  nextAttemptAt: string | null;
  lockedBy: string | null;
  lockedAt: string | null;
  lockExpiresAt: string | null;
  lastOuterSyncEventId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HoldingUnitProjectionWorkerHealth {
  enabled: boolean;
  disabledByEnv: boolean;
  inFlight: boolean;
  intervalMs: number;
  maxCheckpointsPerTick: number;
  lockTtlMs: number;
  startedAt: string | null;
  lastTickStartedAt: string | null;
  lastTickFinishedAt: string | null;
  lastWorkerRunId: string | null;
  lastClaimedAt: string | null;
  lastClaimedCount: number;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
}

export interface GetHoldingUnitProjectionCheckpointStatusRequest {
  companyId: string;
  entityType?: HoldingUnitProjectionCheckpointEntityType;
  status?: HoldingUnitProjectionCheckpointStatus;
  includeCurrent?: boolean;
  limit?: number;
}

export interface HoldingUnitProjectionCheckpointStatusSample {
  checkpoint: HoldingUnitProjectionCheckpointDto;
  lag: number;
  ready: boolean;
  canRequeue: boolean;
}

export interface GetHoldingUnitProjectionCheckpointStatusResponse {
  generatedAt: string;
  ready: boolean;
  countsByStatus: HoldingUnitProjectionCheckpointStatusCounts;
  countsByEntityTypeAndStatus: HoldingUnitProjectionCheckpointCountsByEntityType;
  laggingCount: number;
  dueNowCount: number;
  scheduledRetryCount: number;
  staleProcessingCount: number;
  oldestPendingAt: string | null;
  oldestFailedAt: string | null;
  oldestDeadLetterAt: string | null;
  sampleNonCurrentCheckpoints: HoldingUnitProjectionCheckpointStatusSample[];
  workerHealth: HoldingUnitProjectionWorkerHealth | null;
}

export interface GetHoldingUnitProjectionCheckpointRequest {
  companyId: string;
  entityType: HoldingUnitProjectionCheckpointEntityType;
  entityId: string;
}

export interface GetHoldingUnitProjectionCheckpointResponse {
  generatedAt: string;
  companyId: string;
  entityType: HoldingUnitProjectionCheckpointEntityType;
  entityId: string;
  checkpoint: HoldingUnitProjectionCheckpointDto | null;
  projectedVersion: number;
  targetVersion: number;
  lag: number;
  latestEventStreamVersion: number;
  projectionReadModelVersion: number;
  ready: boolean;
  retry: {
    attempts: number;
    nextAttemptAt: string | null;
    lastErrorCode: string | null;
    lastErrorMessage: string | null;
    lastErrorStage: string | null;
    lastErrorAt: string | null;
  };
  lock: {
    lockedBy: string | null;
    lockedAt: string | null;
    lockExpiresAt: string | null;
    stale: boolean;
  };
  canRequeue: boolean;
}

export interface GetHoldingUnitSnapshotRequest {
  companyId: string;
  unitId: string;
}

export interface GetHoldingUnitSnapshotResponse {
  unit: HoldingUnitSnapshot;
}

export interface BatchGetHoldingUnitCurrentStatesRequest {
  companyId: string;
  unitIds: string[];
}

export interface BatchGetHoldingUnitCurrentStatesItem {
  snapshot: HoldingUnitSnapshot;
  reservation: HoldingUnitReservationState | null;
}

export interface BatchGetHoldingUnitCurrentStatesResponse {
  /** One complete item per requested unit id, preserving request order. */
  items: BatchGetHoldingUnitCurrentStatesItem[];
}

export interface ResolveHoldingUnitByIdentifierRequest {
  companyId: string;
  identifier: string;
}

export interface ResolveHoldingUnitByIdentifierResponseCycle {
  resultType: 'cycle';
  unit: HoldingUnitSnapshot;
  asset: FixedIdAssetRegistryItem | null;
}

export interface ResolveHoldingUnitByIdentifierResponseFixedIdAsset {
  resultType: 'fixedIdAsset';
  unit: null;
  asset: FixedIdAssetRegistryItem;
}

export interface ResolveHoldingUnitByIdentifierResponseNotFound {
  resultType: 'notFound';
  unit: null;
  asset: null;
}

export type ResolveHoldingUnitByIdentifierResponse =
  | ResolveHoldingUnitByIdentifierResponseCycle
  | ResolveHoldingUnitByIdentifierResponseFixedIdAsset
  | ResolveHoldingUnitByIdentifierResponseNotFound;

export interface ListHoldingUnitsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  lifecycleStatus?: HoldingUnitLifecycleStatus;
  includeSuperseded?: boolean;
  searchText?: string;
  yardLocationId?: string | null;
  materialId?: string;
  unitProfileId?: string;
  mode?: HoldingUnitsListMode;
  netState?: HoldingUnitsNetState;
  approxNetLb?: number;
  netToleranceLb?: number;
  approxGrossLb?: number;
  grossToleranceLb?: number;
  statusMode?: HoldingUnitsWorkQueueStatusMode;
  limit?: number;
  cursor?: string;
}

export interface ListHoldingUnitsResponse {
  items: HoldingUnitSnapshot[];
  truncated?: boolean;
  limit?: number;
  nextCursor?: string;
  zeroNetCount?: number;
}

export interface GetHoldingUnitReservationStateRequest {
  companyId: string;
  unitId: string;
}

export interface GetHoldingUnitReservationStateResponse {
  reservation: HoldingUnitReservationState | null;
}

export interface ListHoldingUnitLoadSessionsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  status?: HoldingUnitLoadSessionStatus;
  from?: string; // ISO datetime
  to?: string; // ISO datetime
}

export interface ListHoldingUnitLoadSessionsResponse {
  items: HoldingUnitLoadSession[];
}

export interface GetHoldingUnitLoadSessionDetailRequest {
  companyId: string;
  loadSessionId: string;
}

export interface GetHoldingUnitLoadSessionDetailResponse {
  loadSession: HoldingUnitLoadSession;
  groups: HoldingUnitLoadSessionGroupDetail[];
}

export interface HoldingUnitLoadSessionGroupDetail {
  group: HoldingUnitLoadSessionMaterialGroup;
  totals: HoldingUnitLoadSessionGroupTotals;
  claimStatus: HoldingUnitLoadSessionGroupClaimStatus;
  claimedByOutboundRecord: OutboundRecordReference | null;
  isSelectableForShipment: boolean;
  items: HoldingUnitLoadSessionItem[];
  discrepancyReview: HoldingUnitLoadSessionDiscrepancyReview;
}

export interface ListHoldingUnitLoadSessionShipmentEvidenceOptionsRequest {
  companyId: string;
  storeId: string;
  locationId: string;
  materialId?: string;
  requireMaterialMatch?: boolean;
  limit?: number;
}

export interface HoldingUnitLoadSessionShipmentEvidenceOption {
  loadSession: HoldingUnitLoadSession;
  groups: HoldingUnitLoadSessionGroupDetail[];
}

export interface ListHoldingUnitLoadSessionShipmentEvidenceOptionsResponse {
  items: HoldingUnitLoadSessionShipmentEvidenceOption[];
  truncated: boolean;
  limit: number;
}

export interface ListHoldingUnitLoadSessionAvailableUnitsRequest {
  companyId: string;
  loadSessionId: string;
  groupId: string;
}

export interface ListHoldingUnitLoadSessionAvailableUnitsResponse {
  availableUnits: HoldingUnitSnapshot[];
  eligibleOpenUnits: HoldingUnitSnapshot[];
}

export interface GetHoldingUnitLoadSessionShipmentSeedRequest {
  companyId: string;
  loadSessionId: string;
  groupId: string;
}

export interface HoldingUnitLoadSessionShipmentSeed {
  loadSessionId: string;
  loadSessionName: string | null;
  groupId: string;
  materialId: string;
  targetLb: number;
  locationId: string;
  storeId: string;
}

export type GetHoldingUnitLoadSessionShipmentSeedResponse = HoldingUnitLoadSessionShipmentSeed;

export interface GetInventorySummaryByMaterialAndStatusRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  materialId?: string;
  lifecycleStatus?: HoldingUnitLifecycleStatus;
  businessDate?: string; // ISO date (YYYY-MM-DD)
}

export interface GetInventorySummaryByMaterialAndStatusResponse {
  items: HoldingUnitInventorySummaryItem[];
}

export interface InventoryByYardMaterialItem {
  materialId: string | null;
  totalNetLb: number;
  unitCount: number;
  positiveUnitCount?: number;
  emptyUnitCount?: number;
}

export interface InventoryByYardItem {
  yardLocationId: string | null;
  yardName: string | null;
  yardCode: string | null;
  isYardActive: boolean | null;
  totalNetLb: number;
  unitCount: number;
  positiveUnitCount?: number;
  emptyUnitCount?: number;
  byMaterial?: InventoryByYardMaterialItem[];
}

export interface GetInventoryByYardRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  includeInactiveYards?: boolean;
  lifecycleStatuses?: HoldingUnitLifecycleStatus[];
  materialId?: string;
  groupByMaterial?: boolean;
}

export interface GetInventoryByYardResponse {
  items: InventoryByYardItem[];
  generatedAt: string; // ISO datetime
}

export interface GetAssistedMatchingCandidatesRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  materialId: string;
  lifecycleStatus: HoldingUnitLifecycleStatus;
  grossLb: number;
  toleranceProfileKey: string;
}

export interface GetAssistedMatchingCandidatesResponse {
  items: HoldingUnitAssistedMatchCandidate[];
}

export interface GetHoldingUnitLoadSessionMatchCandidatesRequest {
  companyId: string;
  loadSessionId: string;
  groupId: string;
  grossLb: number;
  toleranceProfileKey?: string;
}

export interface GetHoldingUnitLoadSessionMatchCandidatesResponse {
  autoCandidate: HoldingUnitLoadSessionMatchCandidate | null;
  candidates: HoldingUnitLoadSessionMatchCandidate[];
}
